import { Grid, MenuItem, Typography, Divider } from "@mui/material";
import _ from "lodash";
import React, { PropsWithChildren, ReactElement } from "react";
import { Path, UseControllerProps, useFormContext } from "react-hook-form";

import {
  MetadataFieldTypes,
  MetadataValueSchemaOutput,
  UserSchemaOutput,
} from "@stw/yup-validators";

import CollapsibleDrawer from "~frontend/components/CollapsibleDrawer";
import { UserListSelector } from "~frontend/components/campaignMetadata/UserListSelector";
import {
  RhfSelect,
  RhfTextField,
  RhfYesNoSelect,
} from "~frontend/components/forms/react-hook-form-components";
import { RhfMultiSelectCheckbox } from "~frontend/components/forms/react-hook-form-components/RhfMultiSelectCheckbox";
import { palette } from "~frontend/theme";

type FormData = Record<
  string,
  string | string[] | boolean | UserSchemaOutput | undefined | null
>;

export const EditMetadataValuesSection = ({
  apiFields,
}: PropsWithChildren<{
  campaignId: string;
  apiFields: Record<string, MetadataValueSchemaOutput>;
}>): ReactElement => {
  const { control } = useFormContext();
  const flattenedApiFields = Object.values(apiFields);
  const [requiredFields, optionalFields] = _.partition(
    flattenedApiFields,
    field => field.required
  );
  return (
    <>
      <Typography variant="h0Bold" sx={{ pb: 1, pt: 3 }} component="div">
        Campaign Metadata
      </Typography>
      <Grid container columnSpacing={2}>
        {requiredFields.map(field => (
          <Grid size={{ sm: 8, md: 6 }} key={field.fieldId}>
            <RenderFieldValue
              control={control}
              apiField={field}
              name={field.name}
            />
          </Grid>
        ))}
      </Grid>
      {optionalFields.length > 0 && (
        <CollapsibleDrawer
          title="Optional Metadata Fields"
          startOpen={false}
          variant="slim"
          sxHeader={{ justifyContent: "flex-end", alignItems: "flex-end" }}
          sxHeading={{ fontWeight: 500 }}
        >
          <Divider sx={{ borderColor: palette.lightGray, mt: 1, mb: 1 }} />
          <Grid container columnSpacing={2}>
            {optionalFields.map(field => (
              <Grid size={{ sm: 8, md: 6 }} key={field.fieldId}>
                <RenderFieldValue
                  control={control}
                  apiField={field}
                  name={field.name}
                />
              </Grid>
            ))}
          </Grid>
        </CollapsibleDrawer>
      )}
    </>
  );
};

function RenderFieldValue<T extends FormData>(
  props: UseControllerProps<T> & {
    optional?: boolean;
    bottomLabel?: string;
    characterLimit?: number;
    apiField: MetadataValueSchemaOutput;
    defaultValue?: string;
  }
): ReactElement {
  const { apiField, control, name: fieldName } = props;
  const { type: fieldType, defaultValue } = apiField;
  const label = apiField.required
    ? apiField.name
    : `${apiField.name} (optional)`;
  const name = `metadata.${fieldName}.value` as Path<T>;
  const defaultParams = {
    name,
    control,
    label,
    fullWidth: true,
    bottomLabel: " ",
    sx: { mb: 0 },
  };
  if (fieldType === MetadataFieldTypes.STRING) {
    return <RhfTextField {...defaultParams} />;
  }
  if (fieldType === MetadataFieldTypes.BOOLEAN) {
    return <RhfYesNoSelect {...defaultParams} defaultOption={defaultValue} />;
  }
  if (
    fieldType === MetadataFieldTypes.ENUM ||
    fieldType === MetadataFieldTypes.STATE
  ) {
    const allOptions = [...apiField.configuration.values];
    if (apiField.value) {
      allOptions.push(apiField.value);
    }
    const options = _.uniq(allOptions);
    return (
      <RhfSelect
        {...defaultParams}
        withNoneOption
        // The default render causes form to display the selected values as
        // option (default) & None. If there is no value, an empty input will
        // appear instead.
        renderValue={v => <>{v}</>}
      >
        {options.map(option => (
          <MenuItem key={option} value={option}>
            {defaultValue === option ? `${option} (default)` : option}
          </MenuItem>
        ))}
      </RhfSelect>
    );
  }
  if (
    fieldType === MetadataFieldTypes.ENUM_MULTI ||
    fieldType === MetadataFieldTypes.STATE_MULTI
  ) {
    const allOptions = [...apiField.configuration.values];
    if (apiField.value) {
      apiField.value.forEach(value => allOptions.push(value));
    }
    const options = _.uniq(allOptions)
      .sort()
      .map(option => {
        const label = apiField.defaultValue.includes(option)
          ? `${option} (default)`
          : option;
        return { id: option, label };
      });
    return <RhfMultiSelectCheckbox {...defaultParams} options={options} />;
  }
  if (fieldType === MetadataFieldTypes.USER_LIST) {
    return <UserListSelector {...defaultParams} />;
  }
  return <></>;
}
